import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, money } from "@/lib/api";
import { StatusBadge, EmptyState } from "@/components/Bits";

const ALL = "__all__";

export default function OutstandingList() {
  const [all, setAll] = useState(null);
  const [dropId, setDropId] = useState(ALL);
  useEffect(() => {
    api.get("/outstanding-balances").then((r) => setAll(r.data)).catch(() => setAll([]));
  }, []);
  const drops = useMemo(() => {
    const m = new Map();
    (all || []).forEach((r) => m.set(r.drop_id, r.drop_title));
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [all]);

  if (all === null) return <div className="text-zinc-500 text-sm mt-5">Loading…</div>;
  const rows = dropId === ALL ? all : all.filter((r) => r.drop_id === dropId);
  const total = rows.reduce((s, r) => s + r.balance, 0);
  return (
    <div className="mt-5" data-testid="outstanding-list">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <Select value={dropId} onValueChange={setDropId}>
          <SelectTrigger data-testid="outstanding-drop-filter" className="h-9 w-full sm:w-72 rounded-full bg-zinc-950 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All drops</SelectItem>
            {drops.map(([id, title]) => <SelectItem key={id} value={id} data-testid={`outstanding-drop-option-${id}`}>{title}</SelectItem>)}
          </SelectContent>
        </Select>
        <span data-testid="outstanding-total" className="font-mono text-sm text-pink-300">{rows.length} unpaid · {money(total)} total</span>
      </div>
      <p className="text-xs text-zinc-500 mb-3">Unpaid balance per joiner per drop: claims placed in a set (or special drop items claimed) minus approved payments for that drop. Special drops are tracked separately from regular balances.</p>
      {rows.length === 0 && <EmptyState testId="outstanding-empty" title="Nothing outstanding" hint="Every placed claim is covered by an approved payment." />}
      {rows.length > 0 && (
        <div className="overflow-x-auto rounded-2xl border border-white/5 bg-zinc-900/60">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="text-[11px] uppercase tracking-widest text-zinc-500 border-b border-white/5">
              <tr>
                {["Drop", "Joiner", "Claims / Qty", "Owed", "Approved", "Pending", "Balance due"].map((h, i) => (
                  <th key={h} className={`px-3 py-2.5 font-semibold whitespace-nowrap ${i > 1 ? "text-right" : "text-left"}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.drop_id}-${r.joiner_id}`} data-testid={`outstanding-row-${r.drop_id}-${r.joiner_id}`} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02] transition-colors">
                  <td className="px-3 py-2.5">
                    <Link to={r.kind === "special" ? `/special/${r.drop_id}` : `/drops/${r.drop_id}`} className="font-semibold hover:text-violet-300 transition-colors">{r.drop_title}</Link>
                    <div className="mt-1 flex items-center gap-1.5">
                      <StatusBadge status={r.drop_status} />
                      {r.kind === "special" && <span data-testid={`outstanding-special-tag-${r.drop_id}-${r.joiner_id}`} className="rounded-full bg-amber-400/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-200">Special</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2.5"><Link to={`/joiners/${r.joiner_id}`} className="hover:text-violet-300 transition-colors">{r.joiner_name}</Link></td>
                  <td className="px-3 py-2.5 text-right font-mono">{r.claims}</td>
                  <td className="px-3 py-2.5 text-right font-mono">{money(r.owed)}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-emerald-300">{money(r.paid)}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-amber-300">{r.pending ? money(r.pending) : "—"}</td>
                  <td data-testid={`outstanding-balance-${r.drop_id}-${r.joiner_id}`} className="px-3 py-2.5 text-right font-mono font-bold text-pink-400">{money(r.balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
